import { useMediaQuery, useTheme } from "@mui/material";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { useModals } from "../../../context/ModalContext";
import { appStates, setAppState } from "../../../redux/features/appStateSlice";
import { resetConnectBusinessState } from "../../../redux/features/connectBusinessStateSlice";
import { resetMarkerCount } from "../../../redux/features/coverageSlice";
import { setPlanSelected } from "../../../redux/features/planStateSlice";
import { selectRole } from "../../../redux/features/user/selectors/userState.selectors";
import { BaseModalComponent } from "../BaseModalComponent";

export const HeadlessModalTemplates = ({ toolkitWidgets }) => {
  const { modals } = useModals();

  return (
    <>
      {toolkitWidgets
        .filter((w) => w.headless)
        .map((widget) => {
          const activeModal = modals.find((m) => m.key === widget.modalKey);
          const isPreviewMode = activeModal?.content?.props?.isPreviewMode || false;

          return (
            <BaseModalComponent
              key={widget.key}
              modalKey={widget.modalKey}
              modalLabel={widget.label}
              elementTrigger={null}
              defaultSizes={widget.defaultSizes}
              fitContentHeight={widget.fitContentHeight}
            >
              {isPreviewMode ? React.cloneElement(widget.content, { isPreviewMode: true }) : widget.content}
            </BaseModalComponent>
          );
        })}
    </>
  );
};

//This is a custom hook for the SideBarConnect component, it contains all the smaller hooks and handlers used in the logic of it.
//The location of this file may change in the future

export const useSidebarConnect = ({ basePath }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const role = useSelector(selectRole);

  const { currState } = useSelector((s) => s.appState);

  const { modals, openModal, handleExpand, expandItem } = useModals();

  const [openExit, setOpenExit] = useState(false);
  const [activeButton, setActiveButton] = useState(0);
  const [continueAction, setContinueAction] = useState(null);
  const [toolkitExpanded, setToolkitExpanded] = useState(true);
  const [organizationExpanded, setOrganizationExpanded] = useState(false);
  const [clickTimer, setClickTimer] = useState(null);

  // const [aboutExpanded, setAboutExpanded] = useState(false); //New hook
  // const [activeModal, setActiveModal] = useState(null);

  const handleReset = () => {
    dispatch(resetConnectBusinessState());
    dispatch(resetMarkerCount());
    dispatch(setPlanSelected({ upgradeFreeTrial: false }));
  };

  const guardedNavigate = (index, route, state) => {
    const go = () => {
      setActiveButton(index);
      navigate(`${basePath}${route}`);
      dispatch(setAppState({ currState: state }));
      handleReset();
    };

    if (currState === appStates.FINDACLEANER && role !== "employee") {
      setOpenExit(true);
      setContinueAction(() => go);
    } else {
      go();
    }
  };

  const handleBusiness = (index) => {
    setActiveButton(index);
    navigate(basePath || "/business");
    dispatch(setAppState({ currState: appStates.HOME }));
    handleReset();
  };

  const handleHome = (index) => {
    const go = () => {
      setActiveButton(index);
      navigate("/workforce");
      dispatch(setAppState({ currState: appStates.HOME }));
      handleReset();
    };

    if (currState === appStates.FINDACLEANER && role !== "employee") {
      setOpenExit(true);
      setContinueAction(() => go);
    } else {
      go();
    }
  };

  const handleFlowHub = (index) => {
    const go = () => {
      setActiveButton(index);
      navigate("/flow-hub");
      dispatch(setAppState({ currState: appStates.FLOWHUB }));
      handleReset();
    };

    if (currState === appStates.FINDACLEANER && role !== "employee") {
      setOpenExit(true);
      setContinueAction(() => go);
    } else {
      go();
    }
  };

  const isPageActive = (page, index) => {
    const path = location.pathname;
    const pageName = page.name?.business || page.name?.employee || page.name?.admin || page.name?.virtualReceptionist || page.name?.accountManager || page.name?.superadmin || Object.values(page.name)[0];

    if (pageName === "Flow Hub" && path === "/flow-hub") return true;
    if (pageName === "Dashboard" && (path === "/business" || path === "/workforce" || path === "/admin")) return true;
    if (pageName === "Organization" && path.includes("/organization")) return true;
    if (pageName === "Subscriptions" && path.includes("/plan-center")) return true;
    if (pageName === "Payments" && path.includes("/payments")) return true;
    if (pageName === "Password Vault" && path.includes("/Password-Vault")) return true;
    if (pageName === "My Connections" && path.includes("/my-connections")) return true;
    if (pageName === "Work Setup" && path.includes("/work-setup")) return true;
    if (pageName === "Working Area" && path.includes("/coverage-areas")) return true;
    if (pageName === "Apps Center" && path.includes("/apps-center")) return true;
    if (pageName === "Page Builder" && path.includes("/page-builder")) return true;

    if (
      path === "/flow-hub" ||
      path === "/business" ||
      path === "/workforce" ||
      path === "/admin" ||
      path.includes("/organization") ||
      path.includes("/plan-center") ||
      path.includes("/payments") ||
      path.includes("/Password-Vault") ||
      path.includes("/my-connections") ||
      path.includes("/work-setup") ||
      path.includes("/coverage-areas") ||
      path.includes("/apps-center") ||
      path.includes("/page-builder")
    ) {
      return false;
    }

    return activeButton === index;
  };

  const isMinimized = (modalKey) => modals.some((m) => m.key === modalKey && m.minimized);

  //Normal widgets

  const handleOpenWidget = (widget) => {
    const content = widget.isPreviewMode ? React.cloneElement(widget.content, { isPreviewMode: true }) : widget.content;
    if (widget.headless) {
      openModal(widget.modalKey, content);
      return;
    }
    const targetPath = basePath || "/business";
    if (location.pathname !== targetPath) navigate(targetPath);
    openModal(widget.modalKey, content);
  };

  const handleOpenOrganizationWidget = (widget) => {
    const content = widget.isPreviewMode ? React.cloneElement(widget.content, { isPreviewMode: true }) : widget.content;
    if (widget.headless) {
      openModal(widget.modalKey, content);
      return;
    }
    const targetPath = `${basePath || "/business"}/organization`;
    if (location.pathname !== targetPath) {
      navigate(targetPath);
    }
    openModal(widget.modalKey, content);
  };

  const handlePageItemClick = (index, page, callback) => {
    if (clickTimer) {
      clearTimeout(clickTimer);
      setClickTimer(null);

      if (page.isToolkit) setToolkitExpanded((p) => !p);
      else if (page.isOrganization) setOrganizationExpanded((p) => !p);
    } else {
      const timer = setTimeout(() => {
        callback(index);
        setClickTimer(null);
      }, 250);
      setClickTimer(timer);
    }
  };

  const handleOpenSidebarModal = (item) => {
    openModal(item.modalKey, item.content);
  };

  return {
    // state
    openExit,
    setOpenExit,
    continueAction,
    setContinueAction,
    toolkitExpanded,
    setToolkitExpanded,
    organizationExpanded,
    setOrganizationExpanded,
    expandItem,
    isMediumScreen,
    useModals,
    // aboutExpanded,
    // setAboutExpanded,
    // activeModal,
    // setActiveModal,

    // handlers
    handleExpand,
    guardedNavigate,
    handleBusiness,
    handleHome,
    handleFlowHub,
    handleOpenWidget,
    handleOpenOrganizationWidget,
    handlePageItemClick,
    isPageActive,
    isMinimized,
    handleOpenSidebarModal,

    // data
    role,
  };
};
